import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Users,
  Building2,
  FlaskConical,
  ClipboardList,
  Settings,
  Pill,
  CalendarCheck,
  TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import logo from "@/assets/logo.png";

// Base menu items (these are the SUPER_ADMIN/ADMIN items)
const adminMenu = [
  { label: "Dashboard", icon: Home, to: "/super-admin/dashboard" },
  { label: "Scheduling", icon: CalendarCheck, to: "/super-admin/scheduling" },
  { label: "Vendors", icon: FlaskConical, to: "/super-admin/vendors" },
  { label: "Clinics", icon: Building2, to: "/super-admin/clinics" },
  { label: "Visitors", icon: Users, to: "/super-admin/visitors" },
  { label: "Tests", icon: ClipboardList, to: "/super-admin/tests" },
  { label: "Drug Panels", icon: Pill, to: "/super-admin/drug-panels" },
  // { label: "Reports", icon: ClipboardList, to: "/super-admin/reports" },
  { label: "Analytics", icon: TrendingUp, to: "/super-admin/reports-v2" },
  { label: "Clients", icon: Settings, to: "/super-admin/clients" },
  { label: "Master Data", icon: Settings, to: "/super-admin/master-data" },
];

// CLIENT Menu
const clientAdminMenu = [
  { label: "Dashboard", icon: Home, to: "/client/dashboard" },
  { label: "Scheduling", icon: CalendarCheck, to: "/client/scheduling" },
  { label: "Visitors", icon: ClipboardList, to: "/client/visitors" },
  { label: "Invoices", icon: ClipboardList, to: "/client/reports/invoices" },
  { label: "User Management", icon: Users, to: "/client/employees" },
];

// CLIENT_EMPLOYEE Menu
const clientEmployeeMenu = [
  { label: "Dashboard", icon: Home, to: "/client/dashboard" },
  { label: "Scheduling", icon: CalendarCheck, to: "/client/scheduling" },
  { label: "Visitors", icon: ClipboardList, to: "/client/visitors" },
];

export default function Sidebar({ open }: { open: boolean }) {
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  const getMenu = () => {
    switch (user?.role) {
      case "SUPER_ADMIN":
      case "ADMIN":
        return adminMenu;
      case "CLIENT":
        return clientAdminMenu;
      case "CLIENT_EMPLOYEE":
        return clientEmployeeMenu;
      default:
        return [];
    }
  };

  const menu = getMenu();
  const roleLabel = user?.role ? user.role.replace("_", " ") : "Access Control";

  return (
    <aside
      className={cn(
        "bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 flex flex-col",
        open ? "w-64" : "w-20"
      )}
    >
      {/* Brand */}
      <div className="mt-2 px-4 pb-5 pt-5 flex items-center justify-center">
        <img src={logo} alt="MedXPro" className={cn("object-contain transition-all h-auto my-[-5rem]", open ? "w-auto" : "w-10")} />
      </div>

      {/* Menu */}
      <nav className="mt-2 flex-1 flex flex-col gap-1 px-2">
        {menu.map((item) => {
          const isActive =
            location.pathname === item.to ||
            location.pathname.startsWith(item.to + "/");

          return (
            <Link
              key={item.label}
              to={item.to}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {open && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 text-[11px] text-muted-foreground text-center truncate">
        {open ? roleLabel : roleLabel.substring(0, 1)}
      </div>
    </aside>
  );
}
